/**
 * Admin Launchpad Analytics Page
 *
 * Displays launchpad-wide analytics including:
 * - Launch status counts
 * - Mint statistics and success rates
 * - Revenue tracking (total, settled, pending)
 * - Refund status
 * - Top launches by revenue
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.js';
import { useNotificationStore } from '../store/notificationStore.js';
import {
  getLaunchpadAnalytics,
  exportRefundsCsv,
  type LaunchpadAnalytics,
} from '../services/adminApi.js';

interface StatCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  variant?: 'default' | 'success' | 'warning' | 'error';
}

function StatCard({ label, value, subtext, variant = 'default' }: StatCardProps) {
  const variantStyles = {
    default: 'bg-white border-gray-200',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    error: 'bg-red-50 border-red-200',
  };

  return (
    <div className={`rounded-lg border p-4 ${variantStyles[variant]}`}>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
}

export function AdminLaunchpadAnalyticsPage() {
  const notifications = useNotificationStore();
  const [analytics, setAnalytics] = useState<LaunchpadAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLaunchpadAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
      notifications.error(err.message || 'Failed to load analytics', {
        title: 'Error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportRefunds = async () => {
    try {
      setExportingCsv(true);
      await exportRefundsCsv();
      notifications.success('Refunds CSV downloaded successfully', {
        title: 'Export Complete',
      });
    } catch (err: any) {
      notifications.error(err.message || 'Failed to export refunds', {
        title: 'Export Failed',
      });
    } finally {
      setExportingCsv(false);
    }
  };

  const formatXrp = (value: number) => {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !analytics) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-red-700">{error || 'Failed to load analytics'}</p>
            <button
              onClick={loadAnalytics}
              className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Launchpad Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of all launchpad activity and performance metrics
          </p>
        </div>

        {/* Primary Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total Launches"
            value={analytics.totalLaunches}
          />
          <StatCard
            label="Active Launches"
            value={analytics.activeLaunches}
            variant="success"
          />
          <StatCard
            label="Total Revenue"
            value={`${formatXrp(analytics.totalRevenueXrp)} XRP`}
          />
          <StatCard
            label="Mint Success Rate"
            value={`${analytics.mintSuccessRate.toFixed(1)}%`}
            subtext={`${analytics.successfulMints.toLocaleString()} of ${analytics.totalMints.toLocaleString()} mints`}
            variant={analytics.mintSuccessRate >= 95 ? 'success' : analytics.mintSuccessRate >= 80 ? 'warning' : 'error'}
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Revenue Breakdown */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Revenue Breakdown</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-mono font-semibold">{formatXrp(analytics.totalRevenueXrp)} XRP</span>
              </div>
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <span className="text-gray-600">Settled</span>
                <span className="font-mono text-green-600">{formatXrp(analytics.settledRevenueXrp)} XRP</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Pending Settlement</span>
                <span className="font-mono text-yellow-600">{formatXrp(analytics.pendingSettlementXrp)} XRP</span>
              </div>
            </div>

            {/* Recent activity */}
            <div className="mt-6 rounded-md bg-blue-50 p-3">
              <p className="text-sm font-medium text-blue-800">Last 7 Days</p>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-blue-600">Mints: {analytics.recentMints.toLocaleString()}</span>
                <span className="text-blue-600">Revenue: {formatXrp(analytics.recentRevenueXrp)} XRP</span>
              </div>
            </div>
          </div>

          {/* Refund Status */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Refund Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <span className="text-gray-600">Requiring Refund</span>
                <span className={`font-semibold ${analytics.mintsRequiringRefund > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {analytics.mintsRequiringRefund}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Processed</span>
                <span className="font-semibold text-green-600">{analytics.refundsProcessed}</span>
              </div>
            </div>

            {analytics.mintsRequiringRefund > 0 && (
              <div className="mt-6">
                <button
                  onClick={handleExportRefunds}
                  disabled={exportingCsv}
                  className="w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {exportingCsv ? 'Exporting...' : 'Export Refunds CSV'}
                </button>
              </div>
            )}

            {analytics.mintsRequiringRefund === 0 && analytics.refundsProcessed > 0 && (
              <div className="mt-6 rounded-md bg-green-50 p-3">
                <p className="text-sm text-green-700">All refunds have been processed!</p>
              </div>
            )}
          </div>
        </div>

        {/* Mint Statistics */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Mint Statistics</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-md bg-gray-50 p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{analytics.totalMints.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Mints</p>
            </div>
            <div className="rounded-md bg-green-50 p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{analytics.successfulMints.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Successful</p>
            </div>
            <div className="rounded-md bg-red-50 p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{analytics.failedMints.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Failed</p>
            </div>
            <div className="rounded-md bg-yellow-50 p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">{analytics.pausedLaunches}</p>
              <p className="text-xs text-gray-500">Paused Launches</p>
            </div>
          </div>
        </div>

        {/* Top Launches */}
        {analytics.topLaunches.length > 0 && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Top Launches by Revenue</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase text-gray-500">
                    <th className="py-3 pr-4">Rank</th>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Slug</th>
                    <th className="py-3 pr-4 text-right">Minted</th>
                    <th className="py-3 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {analytics.topLaunches.map((launch, index) => (
                    <tr key={launch.id} className="hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-500">#{index + 1}</td>
                      <td className="py-3 pr-4">
                        <Link
                          to={`/admin/launches/${launch.id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {launch.name}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-gray-500">/{launch.slug}</td>
                      <td className="py-3 pr-4 text-right">{launch.mintedCount.toLocaleString()}</td>
                      <td className="py-3 text-right font-mono font-semibold">
                        {formatXrp(launch.totalRevenueXrp)} XRP
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-6 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
          <span className="text-sm text-gray-600">Need more details?</span>
          <div className="flex gap-4">
            <Link
              to="/admin/launches"
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              View All Launches
            </Link>
            <Link
              to="/admin/settlements"
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              Manage Settlements
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
